import { BehaviorSubject, timer } from 'rxjs';
import { filter, first, map, switchMap } from 'rxjs/operators';

import { MediaMatcher } from '@angular/cdk/layout';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Event, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import {
  ActionModuleEnum,
  ActionsService,
  BaseActionData,
  GlobalLoaderService,
  Task,
} from '@brafton/angular-framework-library';
import { CacheService, OrganizationsService } from '@brafton/atlas-api-client';
import { Organization } from '@brafton/common-framework-library';
import { Feed } from '@brafton/core-api-client';
import { LogentriesService } from '@brafton/logentries-client';
import { BfAuthJwtHelper, BfAuthService } from '@brafton/skynet-angular-security-service';

import { GoogleAnalyticsService } from './core/services/google-analytics.service';
import { PageTitleStrategy } from './core/services/title-strategy.service';
import { SharedSideMenuComponent } from './shared';

// eslint-disable-next-line @typescript-eslint/ban-types
declare let gtag: Function; // <------------Important: the declartion for gtag is required!

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('sideNav') sideNav!: SharedSideMenuComponent;

  appVersion: any;
  authPic: string | null = null;
  globalLoaderAsync = false;
  mobileQuery: MediaQueryList;
  pageOnly = false;
  showHeader = new BehaviorSubject<boolean>(false);
  sideNavFixed = true;
  title = 'Brafton Atlas';
  enabledBetaSearch = false;

  hideSideNavText = false;
  private globalRouterTask!: Task;

  pjson = require('../../package.json');

  constructor(
    public authService: BfAuthService,
    private cacheService: CacheService,
    private loaderService: GlobalLoaderService,
    private logger: LogentriesService,
    private media: MediaMatcher,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private titleStrategy: PageTitleStrategy,
    // private tabTitleService: TabTitleService,
    // Please don't delete this (again...) - it needs to be here (despite not being called in the code) to initialise the GAService and start tracking router events for logging to GA.
    private googleAnalyticsService: GoogleAnalyticsService,
    private actionsService: ActionsService,
    private organizationsService: OrganizationsService,
  ) {
    authService.handleAuthentication();
    this.appendGaTrackingCode('UA-123430518-1');
    this.appendGA4TrackingCode('G-JK0N8YVQ86');
    this.appVersion = this.pjson.version;
    this.mobileQuery = this.media.matchMedia('(max-width: 600px)');

    // this watcher kicks off the global loader during any routing navigation
    // allows routes with waiting resolvers to kick off loading data and showing the loader
    router.events.subscribe((routerEvent: Event) => {
      if (routerEvent instanceof NavigationStart) {
        this.globalRouterTask = this.loaderService.startGlobalTask();
      }
      if (
        routerEvent instanceof NavigationEnd ||
        routerEvent instanceof NavigationCancel ||
        routerEvent instanceof NavigationError
      ) {
        this.loaderService.stopTask(this.globalRouterTask);
      }
    });
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    this.sideNavFixed = scrollTop > 5 ? false : true;
  }

  ngOnInit() {
    const login = this.authService.profileAsync.subscribe((p) => {
      if (this.authService.picture) {
        this.authPic = this.authService.picture as string;
      }
      if (p?.email) {
        this.logger.info(
          `${this.authService.profileId}: User logged in - ${this.authService.email}, with - v${this.appVersion}`,
        );
      }
    });

    // Subscribe to the token async observarble, filter for tokens with contents only, determine the expiry date of the token,
    // Record expiry date globaly, and then repeatedly check every 60 seconds to see if the current time (plus a few minutes) exceeds the
    // expiry date, then call logout - specifically the non-cache destructive logout.
    // This code really needs to go into the bfAuthService so that it's followed automatically
    this.authService.tokenAsync
      .pipe(
        filter((t: any) => t?.length > 0),
        map((t: any) => {
          const tokenExpiry = BfAuthJwtHelper.expirationDate(t);
          return tokenExpiry;
        }),
        switchMap((t: Date) =>
          timer(0, 60000).pipe(
            filter((o: any) => {
              const now = new Date();
              now.setMinutes(now.getMinutes() + 5);
              return now > t;
            }),
            first(),
          ),
        ),
      )
      .subscribe((d) => {
        this.logger.info(
          `${this.authService.profileId}: Token expired so logging out - ${this.authService.email} - v${this.appVersion}`,
        );
        this.authService.logout();
      });

    this.actionsService
      .recieveActionRequests$()
      .pipe(filter((ar: BaseActionData) => ar.actionModule === 'admin'))
      .subscribe((ar: BaseActionData) => {
        // this.actionsService.openModel(ar);
      });
  }

  ngAfterViewChecked() {
    this.loaderService.globalStatusObs().subscribe((r: any) => (this.globalLoaderAsync = r));
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.globalLoaderAsync = false;
  }

  jumpToOrg(feed: Feed) {
    this.organizationsService.getOrganizationFromFeed(feed.id.toString()).subscribe((org: Organization) => {
      this.router.navigate(['/orgs/', org.id]);
    });
  }

  logout() {
    this.cacheService.removeAll();
    this.authService.logout();
  }

  changeUserPassword() {
    this.actionsService.changeActions(
      'ChangePassword',
      ActionModuleEnum.admin,
      undefined,
      undefined,
      undefined,
      undefined,
      'project',
    );
  }

  changeUserDetails() {
    // this.usersService.getUser(this.authService.profileId).pipe(first()).subscribe((res) => {
    //   this.actionsService.changeActions(
    //     'EditUser',
    //     ActionModuleEnum.admin,
    //     undefined,
    //     res as any,
    //     undefined,
    //     undefined,
    //     'project'
    //   );
    // })
  }

  sideNavToggle(event: any) {
    if (event) {
      return (this.hideSideNavText = event);
    }
    this.hideSideNavText = !this.hideSideNavText;
  }

  private appendGaTrackingCode(trackingId: string) {
    //     try {
    //       const script = document.createElement('script');
    //       script.innerHTML =
    //         `
    //         (function(i,s,o,g,r,a,m){i['GoogleAnalyticsObject']=r;i[r]=i[r]||function(){
    //         (i[r].q=i[r].q||[]).push(arguments)},i[r].l=1*new Date();a=s.createElement(o),
    //         m=s.getElementsByTagName(o)[0];a.async=1;a.src=g;m.parentNode.insertBefore(a,m)
    //         })(window,document,'script','https://www.google-analytics.com/analytics.js','ga');
    //         ga('create', '` +
    //         trackingId +
    //         `', 'auto');
    //       `;
    //       document.head.appendChild(script);
    //     } catch (ex) {
    //       console.error('Error appending google analytics');
    //       console.error(ex);
    //     }
    //   }
  }

  appendGA4TrackingCode(gaTrackingId: string) {
    //Read the value of track id obtained in the global config variable.
    //let gaTrackId = this.config.getConfig('trackId');

    //Add custom element of script with following attributes
    const customGtagScriptEle: HTMLScriptElement = document.createElement('script');
    customGtagScriptEle.async = true;
    customGtagScriptEle.src = 'https://www.googletagmanager.com/gtag/js?id=' + gaTrackingId;
    document.head.prepend(customGtagScriptEle);
    gtag('config', gaTrackingId, { send_page_view: false });
  }
}
